<template>
  <div>
    <div v-if="details">
      <b-breadcrumb size="is-large" separator="has-succeeds-separator" aria-label="breadcrumbs">
        <b-breadcrumb-item tag="a" @click.prevent="goToList()">
          {{ $t('secrets.title') }}
        </b-breadcrumb-item>
        <b-breadcrumb-item active>
          {{ name }}
        </b-breadcrumb-item>
      </b-breadcrumb>

      <div class="box">
        <h3 class="title is-5">{{ $t('secrets.details') }}</h3>
        <table class="table is-fullwidth">
          <tbody>
            <tr>
              <td><strong>{{ $t('secrets.name') }}:</strong></td>
              <td>{{ details.name }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('secrets.lastAccessed') }}:</strong></td>
              <td>
                <span v-if="details.accessedAt">{{ $d(new Date(details.accessedAt), 'short') }}</span>
                <span v-else class="has-text-grey">—</span>
              </td>
            </tr>
            <tr>
              <td><strong>{{ $t('secrets.created') }}:</strong></td>
              <td>{{ $d(new Date(details.createdAt), 'short') }}</td>
            </tr>
            <tr>
              <td><strong>{{ $t('secrets.lastUpdated') }}:</strong></td>
              <td>{{ $d(new Date(details.updatedAt), 'short') }}</td>
            </tr>
          </tbody>
        </table>
        <b-button type="is-primary" @click="openUpdateModal">{{ $t('secrets.updateValue') }}</b-button>
      </div>

      <div class="box">
        <h2 class="subtitle">{{ $t('secrets.integrationsUsing') }}</h2>
        <div class="list-table-container">
          <b-table :data="details.integrations || []" striped @click="handleRowClick"
            sort-icon="menu-up" default-sort="name" default-sort-direction="asc">
            <b-table-column field="name" sortable label="Name" v-slot="props">{{ props.row.name }}</b-table-column>
            <b-table-column field="id" sortable label="ID" v-slot="props">{{ props.row.id }}</b-table-column>
            <template #empty>
              <div class="has-text-centered mt-4 mb-4">{{ $t('secrets.noIntegrationsUsing') }}</div>
            </template>
          </b-table>
        </div>
      </div>
    </div>
    <div v-else class="has-text-centered">
      <p class="is-size-4">{{ $t('secrets.notFound') }}</p>
      <b-button @click="goToList()" class="mt-4">
        {{ $t('global.back') }}
      </b-button>
    </div>
  </div>
  <b-modal v-model="updateModalOpen" has-modal-card trap-focus :destroy-on-hide="false" aria-role="dialog" :can-cancel="['escape','x','outside']" :aria-label="$t('secrets.updateValue')" close-button-aria-label="Close" aria-modal>
    <template #default="props">
      <div class="modal-card" style="width: 640px; max-width: 95vw;">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('secrets.updateValue') }}</p>
          <button class="delete" aria-label="close" @click="props.close"></button>
        </header>
        <section class="modal-card-body">
          <b-field :label="$t('secrets.value')">
            <b-input type="textarea" :rows="6" v-model="newValue" :placeholder="$t('secrets.valuePlaceholder')" maxlength="100000" required />
          </b-field>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons">
            <b-button type="is-primary" :disabled="!isValid" @click="saveNewValue">{{ $t('global.save') }}</b-button>
            <b-button @click="props.close">{{ $t('global.close') }}</b-button>
          </div>
        </footer>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, ref, getCurrentInstance } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'SecretDetails',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const name = computed(() => route.params.name as string)
    const store = useStore()
    const details = computed(() => store.state.secretDetails[name.value])
    const vm = getCurrentInstance()

    const load = async () => {
      await store.dispatch('loadSecretDetails', { name: name.value })
    }

    const goToList = () => router.push({name: 'secret-list'})
    
    const goToIntegration = (id: string) => router.push({name: 'integration-edit', params: { id }})
    const handleRowClick = (row: { id: string }) => goToIntegration(row.id)

    // Update value modal state
    const updateModalOpen = ref(false)
    const newValue = ref('')
    const isValid = computed(() => newValue.value.length > 0 && newValue.value.length <= 100000)

    type ToastProxy = { $buefy?: { toast?: { open?: (opts: { message: string; type: string }) => void } } }
    const openToast = (message: string, type: string) => {
      const proxy = vm?.proxy as unknown as ToastProxy
      proxy?.$buefy?.toast?.open?.({ message, type })
    }

    const openUpdateModal = () => {
      newValue.value = ''
      updateModalOpen.value = true
    }

    const saveNewValue = async () => {
      if (!isValid.value) return
      try {
        await store.dispatch('upsertSecret', { name: name.value, value: newValue.value })
        openToast((vm?.proxy as any)?.$t?.('secrets.toastSaved'), 'is-success')
        await load()
        updateModalOpen.value = false
      } catch (e) {
        openToast((vm?.proxy as any)?.$t?.('secrets.toastSaveFailed'), 'is-danger')
      }
    }

    onMounted(load)
    return { name, details, goToList, goToIntegration, handleRowClick, updateModalOpen, newValue, isValid, openUpdateModal, saveNewValue }
  }
})
</script>


